import { zodResolver } from '@hookform/resolvers/zod';
import AddRounded from '@mui/icons-material/AddRounded';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import DeleteOutlineRounded from '@mui/icons-material/DeleteOutlineRounded';
import EditRounded from '@mui/icons-material/EditRounded';
import FolderRounded from '@mui/icons-material/FolderRounded';
import LabelRounded from '@mui/icons-material/LabelRounded';
import PointOfSaleRounded from '@mui/icons-material/PointOfSaleRounded';
import StraightenRounded from '@mui/icons-material/StraightenRounded';
import TuneRounded from '@mui/icons-material/TuneRounded';
import {
  Box,
  Button,
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Skeleton,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import { Controller, useForm, useWatch, type Path } from 'react-hook-form';
import { useNavigate, useSearchParams } from 'react-router';
import { api, unwrap, type Schemas } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ImageEditor, ImageFill } from '../../components/images';
import { EmptyState, PageHeader } from '../../components/states';
import { useNotify } from '../../lib/notify';
import { useShop } from '../../shop/ShopProvider';
import {
  catalogKeys,
  categoryTree,
  categoryWithDescendants,
  EMPTY_SELECT_SLOTS,
  idempotency,
  type Attribute,
  type Brand,
  type Category,
  type Unit,
} from './model';
import { useAttributes, useBrands, useCategories, usePosCategories, useUnits } from './queries';
import {
  applyFieldErrors,
  attributeFormSchema,
  brandFormSchema,
  CATEGORY_ACCOUNT_FIELDS,
  productCategoryFormSchema,
  optionsFromText,
  posCategoryFormSchema,
  unitFormSchema,
  type AttributeFormValues,
  type BrandFormValues,
  type CategoryFormValues,
  type PosCategoryFormValues,
  type UnitFormValues,
} from './setupModel';
import { CategorySelect } from './ui';

type TabKey = 'categories' | 'pos-categories' | 'brands' | 'units' | 'attributes';
const TABS: { key: TabKey; label: string }[] = [
  { key: 'categories', label: 'Categories' },
  { key: 'pos-categories', label: 'POS categories' },
  { key: 'brands', label: 'Brands' },
  { key: 'units', label: 'Units' },
  { key: 'attributes', label: 'Attributes' },
];

/** /products/setup — the lists products are built from: categories, brands, units, attributes. */
export function CatalogSetupPage() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const tabParam = params.get('tab') as TabKey | null;
  const { settings } = useShop();
  // POS categories only matter while the shop has the point of sale switched on.
  const tabs = TABS.filter((t) => t.key !== 'pos-categories' || settings?.posEnabled !== false);
  const tab: TabKey = tabs.some((t) => t.key === tabParam) ? (tabParam as TabKey) : 'categories';
  return (
    <>
      <Button
        size="small"
        color="inherit"
        startIcon={<ArrowBackRounded />}
        onClick={() => void navigate('/products')}
        sx={{ mb: 1.5, ml: -1, color: 'text.secondary' }}
      >
        Products
      </Button>
      <PageHeader
        title="Catalog setup"
        subtitle="Categories, brands, units of measure and variant attributes used across your products."
      />
      <Card sx={{ mb: 2.5, px: { xs: 1, sm: 2 } }}>
        <Tabs
          value={tab}
          onChange={(_, v: TabKey) => setParams(v === 'categories' ? {} : { tab: v })}
          variant="scrollable"
          allowScrollButtonsMobile
        >
          {tabs.map((t) => (
            <Tab key={t.key} value={t.key} label={t.label} />
          ))}
        </Tabs>
      </Card>
      {tab === 'categories' ? (
        <CategoriesTab />
      ) : tab === 'pos-categories' ? (
        <PosCategoriesTab />
      ) : tab === 'brands' ? (
        <BrandsTab />
      ) : tab === 'units' ? (
        <UnitsTab />
      ) : (
        <AttributesTab />
      )}
    </>
  );
}

// --- shared bits -------------------------------------------------------------------------

function ListCard({
  title,
  hint,
  action,
  loading,
  empty,
  children,
}: {
  title: string;
  hint: string;
  action?: ReactNode;
  loading: boolean;
  empty: ReactNode | null;
  children: ReactNode;
}) {
  return (
    <Card>
      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1.5}
        sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider', alignItems: { sm: 'center' } }}
      >
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{ fontSize: '1.0625rem' }}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {hint}
          </Typography>
        </Box>
        {action}
      </Stack>
      {loading ? (
        <Box sx={{ p: 2.5 }}>
          {[0, 1, 2, 3].map((i) => (
            <Skeleton key={i} height={44} />
          ))}
        </Box>
      ) : (
        (empty ?? children)
      )}
    </Card>
  );
}

function Row({
  icon,
  depth = 0,
  title,
  meta,
  chips,
  actions,
}: {
  icon: ReactNode;
  depth?: number;
  title: ReactNode;
  meta?: ReactNode;
  chips?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <Stack
      direction="row"
      spacing={1.5}
      sx={{
        px: 2.5,
        py: 1.5,
        pl: 2.5 + depth * 3,
        alignItems: 'center',
        borderBottom: 1,
        borderColor: 'divider',
        '&:last-child': { borderBottom: 0 },
        '&:hover': { bgcolor: 'action.hover' },
      }}
    >
      <Box
        sx={(t) => {
          const c = (t.vars ?? t).palette.primary.main;
          return {
            width: 34,
            height: 34,
            flexShrink: 0,
            borderRadius: '10px',
            overflow: 'hidden',
            display: 'grid',
            placeItems: 'center',
            color: c,
            bgcolor: `color-mix(in srgb, ${c} 10%, transparent)`,
            '& svg': { fontSize: 18 },
          };
        }}
      >
        {icon}
      </Box>
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
          {title}
        </Typography>
        {meta && (
          <Typography variant="caption" color="text.secondary" noWrap component="div">
            {meta}
          </Typography>
        )}
      </Box>
      {chips && (
        <Stack direction="row" spacing={0.75} sx={{ display: { xs: 'none', sm: 'flex' } }}>
          {chips}
        </Stack>
      )}
      {actions && <Stack direction="row">{actions}</Stack>}
    </Stack>
  );
}

function SimpleDialog({
  open,
  title,
  busy,
  submitLabel,
  onClose,
  onSubmit,
  children,
}: {
  open: boolean;
  title: string;
  busy: boolean;
  submitLabel: string;
  onClose: () => void;
  onSubmit: () => void;
  children: ReactNode;
}) {
  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} maxWidth="xs" fullWidth>
      <Box
        component="form"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          onSubmit();
        }}
      >
        <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
        <DialogContent>
          <Stack spacing={2.25} sx={{ pt: 1 }}>
            {children}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button variant="outlined" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" loading={busy}>
            {submitLabel}
          </Button>
        </DialogActions>
      </Box>
    </Dialog>
  );
}

function EditButton({ label, onClick }: { label: string; onClick: () => void }) {
  return (
    <Tooltip title="Edit">
      <IconButton size="small" aria-label={label} onClick={onClick}>
        <EditRounded fontSize="small" />
      </IconButton>
    </Tooltip>
  );
}

// --- categories --------------------------------------------------------------------------

function CategoriesTab() {
  const { can } = useAuth();
  const categories = useCategories();
  const notify = useNotify();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Category | 'new' | null>(null);
  const [deleting, setDeleting] = useState<Category | null>(null);
  const list = categories.data ?? [];
  const tree = categoryTree(list);
  const canAdd = can('products.create') || can('products.update');
  const canEdit = can('products.update');
  const childCount = (id: string) => list.filter((c) => c.parentId === id).length;

  return (
    <>
      <ListCard
        title="Categories"
        hint="Group products, e.g. Tiles › Floor tiles. Nest as deep as you like."
        loading={categories.isLoading}
        action={
          canAdd && (
            <Button
              variant="contained"
              startIcon={<AddRounded />}
              onClick={() => setEditing('new')}
            >
              New category
            </Button>
          )
        }
        empty={
          tree.length === 0 ? (
            <EmptyState
              icon={<FolderRounded />}
              title="No categories yet"
              body="Categories make the product list easy to filter and reports easy to read."
              action={
                canAdd ? { label: 'Add a category', onClick: () => setEditing('new') } : undefined
              }
            />
          ) : null
        }
      >
        {tree.map((c) => (
          <Row
            key={c.id}
            icon={<ImageFill url={c.imageUrl} fallback={<FolderRounded />} />}
            depth={c.depth}
            title={c.name}
            meta={
              c.depth > 0
                ? c.path
                : childCount(c.id)
                  ? `${String(childCount(c.id))} subcategor${childCount(c.id) === 1 ? 'y' : 'ies'}`
                  : 'Top level'
            }
            actions={
              canEdit && (
                <>
                  <EditButton label={`Edit ${c.name}`} onClick={() => setEditing(c)} />
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      aria-label={`Delete ${c.name}`}
                      onClick={() => setDeleting(c)}
                    >
                      <DeleteOutlineRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )
            }
          />
        ))}
      </ListCard>
      <CategoryDialog
        open={editing !== null}
        // The listed row, so a new image shows while the dialog is open.
        category={editing === 'new' ? null : (list.find((c) => c.id === editing?.id) ?? editing)}
        categories={list}
        onClose={() => setEditing(null)}
      />
      <ConfirmDialog
        open={!!deleting}
        danger
        title={`Delete ${deleting?.name ?? 'category'}?`}
        body="Only empty categories can be deleted — move its products and subcategories first."
        confirmLabel="Delete"
        onConfirm={() => {
          const c = deleting;
          if (!c) return Promise.resolve();
          return unwrap(
            api.DELETE('/categories/{id}', {
              params: { path: { id: c.id }, query: { version: c.version } },
            }),
          )
            .then(async () => {
              notify.success(`${c.name} deleted`);
              await qc.invalidateQueries({ queryKey: catalogKeys.categories });
            })
            .catch((e: unknown) => notify.error(e));
        }}
        onClose={() => setDeleting(null)}
      />
    </>
  );
}

function CategoryDialog({
  open,
  category,
  categories,
  onClose,
}: {
  open: boolean;
  category: Category | null;
  categories: Category[];
  onClose: () => void;
}) {
  const { can } = useAuth();
  const notify = useNotify();
  const qc = useQueryClient();
  // Accounts per category (ADR-0016) are for whoever manages the chart of accounts.
  const canAccounts = can('accounts.manage');
  const accounts = useQuery({
    queryKey: ['accounts'],
    queryFn: () => unwrap(api.GET('/accounts')),
    enabled: open && canAccounts,
  });
  const initial = (): CategoryFormValues => ({
    name: category?.name ?? '',
    parentId: category?.parentId ?? '',
    incomeAccountId: category?.incomeAccountId ?? '',
    cogsAccountId: category?.cogsAccountId ?? '',
    valuationAccountId: category?.valuationAccountId ?? '',
  });
  const { register, control, handleSubmit, reset, setError, formState } =
    useForm<CategoryFormValues>({
      resolver: zodResolver(productCategoryFormSchema),
      defaultValues: initial(),
    });
  const accountBody = (v: CategoryFormValues) =>
    canAccounts
      ? {
          incomeAccountId: v.incomeAccountId || null,
          cogsAccountId: v.cogsAccountId || null,
          valuationAccountId: v.valuationAccountId || null,
        }
      : {};
  useEffect(() => {
    if (open) reset(initial());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when opened
  }, [open, category]);
  const save = useMutation({
    mutationFn: (v: CategoryFormValues) =>
      category
        ? unwrap(
            api.PATCH('/categories/{id}', {
              params: { path: { id: category.id } },
              body: {
                name: v.name.trim(),
                parentId: v.parentId || null,
                ...accountBody(v),
                version: category.version,
              },
            }),
          )
        : unwrap(
            api.POST('/categories', {
              params: { header: idempotency() },
              body: { name: v.name.trim(), parentId: v.parentId || null, ...accountBody(v) },
            }),
          ),
    onSuccess: async (saved) => {
      notify.success(category ? `${saved.name} saved` : `${saved.name} added`);
      await qc.invalidateQueries({ queryKey: catalogKeys.categories });
      await qc.invalidateQueries({ queryKey: catalogKeys.products });
      onClose();
    },
    onError: (e) => {
      if (
        !applyFieldErrors(
          e,
          ['name', 'parentId', ...CATEGORY_ACCOUNT_FIELDS.map((a) => a.field)],
          (f, m) => setError(f as Path<CategoryFormValues>, m),
        )
      )
        notify.error(e);
    },
  });
  return (
    <SimpleDialog
      open={open}
      title={category ? `Edit ${category.name}` : 'New category'}
      busy={save.isPending}
      submitLabel={category ? 'Save' : 'Add category'}
      onClose={onClose}
      onSubmit={() => void handleSubmit((v) => save.mutate(v))()}
    >
      {category && (
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <ImageEditor
            target={{ kind: 'category', id: category.id }}
            url={category.imageUrl}
            canEdit={can('products.update')}
            label={category.name}
          >
            <Box
              sx={(t) => {
                const c = (t.vars ?? t).palette.primary.main;
                return {
                  width: 72,
                  height: 72,
                  borderRadius: '16px',
                  overflow: 'hidden',
                  display: 'grid',
                  placeItems: 'center',
                  color: c,
                  bgcolor: `color-mix(in srgb, ${c} 10%, transparent)`,
                  '& svg': { fontSize: 30 },
                };
              }}
            >
              <ImageFill url={category.imageUrl} alt={category.name} fallback={<FolderRounded />} />
            </Box>
          </ImageEditor>
          <Typography variant="body2" color="text.secondary">
            Category image, shown on the point-of-sale category buttons. JPEG, PNG or WebP up to 5
            MB.
          </Typography>
        </Stack>
      )}
      <TextField
        label="Name"
        autoFocus
        required
        {...register('name')}
        error={!!formState.errors.name}
        helperText={formState.errors.name?.message}
      />
      <Controller
        control={control}
        name="parentId"
        render={({ field }) => (
          <CategorySelect
            label="Inside"
            noneLabel="Top level"
            categories={categories}
            exclude={category ? categoryWithDescendants(categories, category.id) : undefined}
            value={field.value}
            onChange={field.onChange}
            error={!!formState.errors.parentId}
            helperText={formState.errors.parentId?.message}
          />
        )}
      />
      {canAccounts && (
        <>
          <Typography variant="subtitle2" sx={{ pt: 1 }}>
            Accounts
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Where this category’s sales, cost of sales and stock value are posted. Left empty, the
            parent category’s account is used, then the shop’s standard account. Only accounts set
            for product categories in the chart of accounts are listed; a change applies to new
            postings.
          </Typography>
          {CATEGORY_ACCOUNT_FIELDS.map(({ field: name, role, label }) => {
            const all = accounts.data?.data ?? [];
            const standard = all.find((a) => a.systemKey === role);
            return (
              <Controller
                key={name}
                control={control}
                name={name}
                render={({ field }) => (
                  <TextField
                    select
                    label={label}
                    value={field.value}
                    onChange={field.onChange}
                    error={!!formState.errors[name]}
                    helperText={formState.errors[name]?.message}
                    slotProps={{ select: { displayEmpty: true }, inputLabel: { shrink: true } }}
                  >
                    <MenuItem value="">
                      {standard
                        ? `From the parent, else ${standard.code} ${standard.name}`
                        : 'From the parent category'}
                    </MenuItem>
                    {all
                      .filter((a) => a.role === role && (a.isActive || a.id === field.value))
                      .map((a) => (
                        <MenuItem key={a.id} value={a.id}>
                          {a.code} {a.name}
                        </MenuItem>
                      ))}
                  </TextField>
                )}
              />
            );
          })}
        </>
      )}
    </SimpleDialog>
  );
}

// --- POS categories ----------------------------------------------------------------------

type PosCategory = Schemas['PosCategory'];

function PosCategoriesTab() {
  const { can } = useAuth();
  const categories = usePosCategories();
  const notify = useNotify();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<PosCategory | 'new' | null>(null);
  const [deleting, setDeleting] = useState<PosCategory | null>(null);
  const list = categories.data ?? [];
  const canEdit = can('products.update') || can('pos.manage');
  const depth = (c: PosCategory) => c.path.split(' / ').length - 1;

  return (
    <>
      <ListCard
        title="POS categories"
        hint="The tabs on the point of sale register, separate from your catalog categories. Pick one on each product that is available in POS."
        loading={categories.isLoading}
        action={
          canEdit && (
            <Button
              variant="contained"
              startIcon={<AddRounded />}
              onClick={() => setEditing('new')}
            >
              New POS category
            </Button>
          )
        }
        empty={
          list.length === 0 ? (
            <EmptyState
              icon={<PointOfSaleRounded />}
              title="No POS categories yet"
              body="Until you add some, the register groups products by their catalog category."
              action={
                canEdit
                  ? { label: 'Add a POS category', onClick: () => setEditing('new') }
                  : undefined
              }
            />
          ) : null
        }
      >
        {list.map((c) => (
          <Row
            key={c.id}
            icon={<PointOfSaleRounded />}
            depth={depth(c)}
            title={c.name}
            meta={`${depth(c) > 0 ? `${c.path} · ` : ''}${String(c.productCount)} product${c.productCount === 1 ? '' : 's'}`}
            chips={<Chip size="small" variant="outlined" label={`Order ${String(c.sortOrder)}`} />}
            actions={
              canEdit && (
                <>
                  <EditButton label={`Edit ${c.name}`} onClick={() => setEditing(c)} />
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      aria-label={`Delete ${c.name}`}
                      onClick={() => setDeleting(c)}
                    >
                      <DeleteOutlineRounded fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </>
              )
            }
          />
        ))}
      </ListCard>
      <PosCategoryDialog
        open={editing !== null}
        category={editing === 'new' ? null : editing}
        categories={list}
        onClose={() => setEditing(null)}
      />
      <ConfirmDialog
        open={!!deleting}
        danger
        title={`Delete ${deleting?.name ?? 'POS category'}?`}
        body="Only empty POS categories can be deleted — move its products and subcategories first."
        confirmLabel="Delete"
        onConfirm={() => {
          const c = deleting;
          if (!c) return Promise.resolve();
          return unwrap(
            api.DELETE('/pos/categories/{id}', {
              params: { path: { id: c.id }, query: { version: c.version } },
            }),
          )
            .then(async () => {
              notify.success(`${c.name} deleted`);
              await qc.invalidateQueries({ queryKey: catalogKeys.posCategories });
            })
            .catch((e: unknown) => notify.error(e));
        }}
        onClose={() => setDeleting(null)}
      />
    </>
  );
}

function PosCategoryDialog({
  open,
  category,
  categories,
  onClose,
}: {
  open: boolean;
  category: PosCategory | null;
  categories: PosCategory[];
  onClose: () => void;
}) {
  const notify = useNotify();
  const qc = useQueryClient();
  const initial = (): PosCategoryFormValues => ({
    name: category?.name ?? '',
    parentId: category?.parentId ?? '',
    sortOrder: String(category?.sortOrder ?? 0),
  });
  const { register, control, handleSubmit, reset, setError, formState } =
    useForm<PosCategoryFormValues>({
      resolver: zodResolver(posCategoryFormSchema),
      defaultValues: initial(),
    });
  useEffect(() => {
    if (open) reset(initial());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when opened
  }, [open, category]);
  // Not under itself or its own subcategories.
  const excluded = new Set<string>();
  if (category)
    for (const c of categories)
      if (c.id === category.id || c.path.startsWith(`${category.path} / `)) excluded.add(c.id);
  const save = useMutation({
    mutationFn: (v: PosCategoryFormValues) => {
      const body = {
        name: v.name.trim(),
        parentId: v.parentId || null,
        sortOrder: Number(v.sortOrder),
      };
      return category
        ? unwrap(
            api.PATCH('/pos/categories/{id}', {
              params: { path: { id: category.id } },
              body: { ...body, version: category.version },
            }),
          )
        : unwrap(api.POST('/pos/categories', { params: { header: idempotency() }, body }));
    },
    onSuccess: async (saved) => {
      notify.success(category ? `${saved.name} saved` : `${saved.name} added`);
      await qc.invalidateQueries({ queryKey: catalogKeys.posCategories });
      await qc.invalidateQueries({ queryKey: catalogKeys.products });
      onClose();
    },
    onError: (e) => {
      if (
        !applyFieldErrors(e, ['name', 'parentId', 'sortOrder'], (f, m) =>
          setError(f as Path<PosCategoryFormValues>, m),
        )
      )
        notify.error(e);
    },
  });
  return (
    <SimpleDialog
      open={open}
      title={category ? `Edit ${category.name}` : 'New POS category'}
      busy={save.isPending}
      submitLabel={category ? 'Save' : 'Add POS category'}
      onClose={onClose}
      onSubmit={() => void handleSubmit((v) => save.mutate(v))()}
    >
      <TextField
        label="Name"
        autoFocus
        required
        {...register('name')}
        error={!!formState.errors.name}
        helperText={formState.errors.name?.message}
      />
      <Controller
        control={control}
        name="parentId"
        render={({ field }) => (
          <TextField
            select
            label="Inside"
            value={field.value}
            onChange={field.onChange}
            error={!!formState.errors.parentId}
            helperText={formState.errors.parentId?.message}
            slotProps={EMPTY_SELECT_SLOTS}
          >
            <MenuItem value="">
              <Typography color="text.secondary">Top level</Typography>
            </MenuItem>
            {categories
              .filter((c) => !excluded.has(c.id))
              .map((c) => (
                <MenuItem key={c.id} value={c.id}>
                  {c.path}
                </MenuItem>
              ))}
          </TextField>
        )}
      />
      <TextField
        label="Tab order"
        {...register('sortOrder')}
        error={!!formState.errors.sortOrder}
        helperText={formState.errors.sortOrder?.message ?? 'Lower numbers come first, then by name'}
        slotProps={{ htmlInput: { inputMode: 'numeric' } }}
      />
    </SimpleDialog>
  );
}

// --- brands ------------------------------------------------------------------------------

function BrandsTab() {
  const { can } = useAuth();
  const brands = useBrands();
  const [editing, setEditing] = useState<Brand | 'BRAND' | 'MANUFACTURER' | null>(null);
  const canAdd = can('products.create') || can('products.update');
  const canEdit = can('products.update');
  const list = [...(brands.data ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2.5,
        gridTemplateColumns: { xs: 'minmax(0,1fr)', lg: '1fr 1fr' },
        alignItems: 'start',
      }}
    >
      {(['BRAND', 'MANUFACTURER'] as const).map((kind) => {
        const rows = list.filter((b) => b.kind === kind);
        const word = kind === 'BRAND' ? 'brand' : 'manufacturer';
        return (
          <ListCard
            key={kind}
            title={kind === 'BRAND' ? 'Brands' : 'Manufacturers'}
            hint={
              kind === 'BRAND'
                ? 'The name on the box, e.g. Master Tiles.'
                : 'Who makes it, when that matters.'
            }
            loading={brands.isLoading}
            action={
              canAdd && (
                <Button
                  variant="outlined"
                  startIcon={<AddRounded />}
                  onClick={() => setEditing(kind)}
                >
                  Add
                </Button>
              )
            }
            empty={
              rows.length === 0 ? (
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <Typography color="text.secondary">No {word}s yet.</Typography>
                </Box>
              ) : null
            }
          >
            {rows.map((b) => (
              <Row
                key={b.id}
                icon={<LabelRounded />}
                title={b.name}
                chips={!b.isActive && <Chip size="small" label="Inactive" variant="outlined" />}
                actions={
                  canEdit && <EditButton label={`Edit ${b.name}`} onClick={() => setEditing(b)} />
                }
              />
            ))}
          </ListCard>
        );
      })}
      <BrandDialog
        open={editing !== null}
        brand={typeof editing === 'object' ? editing : null}
        kind={typeof editing === 'string' ? editing : (editing?.kind ?? 'BRAND')}
        onClose={() => setEditing(null)}
      />
    </Box>
  );
}

function BrandDialog({
  open,
  brand,
  kind,
  onClose,
}: {
  open: boolean;
  brand: Brand | null;
  kind: 'BRAND' | 'MANUFACTURER';
  onClose: () => void;
}) {
  const notify = useNotify();
  const qc = useQueryClient();
  const initial = (): BrandFormValues => ({
    name: brand?.name ?? '',
    isActive: brand?.isActive ?? true,
  });
  const { register, control, handleSubmit, reset, setError, formState } = useForm<BrandFormValues>({
    resolver: zodResolver(brandFormSchema),
    defaultValues: initial(),
  });
  useEffect(() => {
    if (open) reset(initial());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when opened
  }, [open, brand]);
  const word = kind === 'BRAND' ? 'brand' : 'manufacturer';
  const save = useMutation({
    mutationFn: (v: BrandFormValues) =>
      brand
        ? unwrap(
            api.PATCH('/brands/{id}', {
              params: { path: { id: brand.id } },
              body: { name: v.name.trim(), isActive: v.isActive, version: brand.version },
            }),
          )
        : unwrap(
            api.POST('/brands', {
              params: { header: idempotency() },
              body: { name: v.name.trim(), kind },
            }),
          ),
    onSuccess: async (saved) => {
      notify.success(brand ? `${saved.name} saved` : `${saved.name} added`);
      await qc.invalidateQueries({ queryKey: catalogKeys.brands });
      await qc.invalidateQueries({ queryKey: catalogKeys.products });
      onClose();
    },
    onError: (e) => {
      if (!applyFieldErrors(e, ['name'], (f, m) => setError(f as Path<BrandFormValues>, m)))
        notify.error(e);
    },
  });
  return (
    <SimpleDialog
      open={open}
      title={brand ? `Edit ${brand.name}` : `New ${word}`}
      busy={save.isPending}
      submitLabel={brand ? 'Save' : `Add ${word}`}
      onClose={onClose}
      onSubmit={() => void handleSubmit((v) => save.mutate(v))()}
    >
      <TextField
        label="Name"
        autoFocus
        required
        {...register('name')}
        error={!!formState.errors.name}
        helperText={formState.errors.name?.message}
      />
      {brand && (
        <Controller
          control={control}
          name="isActive"
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
              }
              label="Active — offered on products"
            />
          )}
        />
      )}
    </SimpleDialog>
  );
}

// --- units -------------------------------------------------------------------------------

function UnitsTab() {
  const { can } = useAuth();
  const units = useUnits();
  const [editing, setEditing] = useState<Unit | 'new' | null>(null);
  const canAdd = can('products.create') || can('products.update');
  const canEdit = can('products.update');
  const list = [...(units.data ?? [])].sort((a, b) => a.name.localeCompare(b.name));
  return (
    <>
      <ListCard
        title="Units of measure"
        hint="Stock is counted in a product’s base unit. Pack sizes on each product convert to it."
        loading={units.isLoading}
        action={
          canAdd && (
            <Button
              variant="contained"
              startIcon={<AddRounded />}
              onClick={() => setEditing('new')}
            >
              New unit
            </Button>
          )
        }
        empty={null}
      >
        {list.map((u) => (
          <Row
            key={u.id}
            icon={<StraightenRounded />}
            title={
              <>
                {u.name}{' '}
                <Box component="span" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  ({u.symbol})
                </Box>
              </>
            }
            meta={u.allowsFraction ? 'Fractions allowed, e.g. 2.5' : 'Whole numbers only'}
            chips={!u.isActive && <Chip size="small" label="Inactive" variant="outlined" />}
            actions={
              canEdit && <EditButton label={`Edit ${u.name}`} onClick={() => setEditing(u)} />
            }
          />
        ))}
      </ListCard>
      <UnitDialog
        open={editing !== null}
        unit={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
      />
    </>
  );
}

function UnitDialog({
  open,
  unit,
  onClose,
}: {
  open: boolean;
  unit: Unit | null;
  onClose: () => void;
}) {
  const notify = useNotify();
  const qc = useQueryClient();
  const initial = (): UnitFormValues => ({
    name: unit?.name ?? '',
    symbol: unit?.symbol ?? '',
    allowsFraction: unit?.allowsFraction ?? false,
    isActive: unit?.isActive ?? true,
  });
  const { register, control, handleSubmit, reset, setError, formState } = useForm<UnitFormValues>({
    resolver: zodResolver(unitFormSchema),
    defaultValues: initial(),
  });
  useEffect(() => {
    if (open) reset(initial());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when opened
  }, [open, unit]);
  const save = useMutation({
    mutationFn: (v: UnitFormValues) =>
      unit
        ? unwrap(
            api.PATCH('/units/{id}', {
              params: { path: { id: unit.id } },
              body: {
                name: v.name.trim(),
                symbol: v.symbol.trim(),
                ...(v.allowsFraction !== unit.allowsFraction
                  ? { allowsFraction: v.allowsFraction }
                  : {}),
                isActive: v.isActive,
                version: unit.version,
              },
            }),
          )
        : unwrap(
            api.POST('/units', {
              params: { header: idempotency() },
              body: {
                name: v.name.trim(),
                symbol: v.symbol.trim(),
                allowsFraction: v.allowsFraction,
              },
            }),
          ),
    onSuccess: async (saved) => {
      notify.success(unit ? `${saved.name} saved` : `${saved.name} added`);
      await qc.invalidateQueries({ queryKey: catalogKeys.units });
      onClose();
    },
    onError: (e) => {
      if (
        !applyFieldErrors(e, ['name', 'symbol', 'allowsFraction'], (f, m) =>
          setError(f as Path<UnitFormValues>, m),
        )
      )
        notify.error(e);
    },
  });
  return (
    <SimpleDialog
      open={open}
      title={unit ? `Edit ${unit.name}` : 'New unit'}
      busy={save.isPending}
      submitLabel={unit ? 'Save' : 'Add unit'}
      onClose={onClose}
      onSubmit={() => void handleSubmit((v) => save.mutate(v))()}
    >
      <Stack direction="row" spacing={2}>
        <TextField
          label="Name"
          autoFocus
          required
          fullWidth
          placeholder="Carton"
          {...register('name')}
          error={!!formState.errors.name}
          helperText={formState.errors.name?.message}
        />
        <TextField
          label="Symbol"
          required
          placeholder="ctn"
          {...register('symbol')}
          error={!!formState.errors.symbol}
          helperText={formState.errors.symbol?.message}
          sx={{ width: 120, flexShrink: 0 }}
        />
      </Stack>
      <Controller
        control={control}
        name="allowsFraction"
        render={({ field, fieldState }) => (
          <Box>
            <FormControlLabel
              control={
                <Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
              }
              label="Allow fractions (e.g. 2.5 kg)"
            />
            <Typography
              variant="caption"
              color={fieldState.error ? 'error' : 'text.secondary'}
              sx={{ display: 'block' }}
            >
              {fieldState.error?.message ?? 'Can’t be changed once products use this unit.'}
            </Typography>
          </Box>
        )}
      />
      {unit && (
        <Controller
          control={control}
          name="isActive"
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
              }
              label="Active — offered on new products"
            />
          )}
        />
      )}
    </SimpleDialog>
  );
}

// --- attributes --------------------------------------------------------------------------

function AttributesTab() {
  const { can } = useAuth();
  const attributes = useAttributes();
  const notify = useNotify();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<Attribute | 'new' | null>(null);
  const [adding, setAdding] = useState<Attribute | null>(null);
  const [optionText, setOptionText] = useState('');
  const canAdd = can('products.create') || can('products.update');
  const canEdit = can('products.update');
  const list = [...(attributes.data ?? [])].sort((a, b) => a.name.localeCompare(b.name));

  const addOption = useMutation({
    mutationFn: ({ attribute, value }: { attribute: Attribute; value: string }) =>
      unwrap(
        api.POST('/attributes/{id}/options', {
          params: { path: { id: attribute.id }, header: idempotency() },
          body: { value, sortOrder: attribute.options.length },
        }),
      ),
    onSuccess: async (_, { value }) => {
      notify.success(`${value} added`);
      setOptionText('');
      setAdding(null);
      await qc.invalidateQueries({ queryKey: catalogKeys.attributes });
    },
    onError: (e) => notify.error(e),
  });
  const toggleOption = useMutation({
    mutationFn: ({
      attribute,
      optionId,
      isActive,
    }: {
      attribute: Attribute;
      optionId: string;
      isActive: boolean;
    }) =>
      unwrap(
        api.PATCH('/attributes/{id}/options/{optionId}', {
          params: { path: { id: attribute.id, optionId } },
          body: { isActive },
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: catalogKeys.attributes }),
    onError: (e) => notify.error(e),
  });

  return (
    <>
      <ListCard
        title="Variant attributes"
        hint="Describe variants consistently — Size, Finish, Colour. Options keep spelling uniform."
        loading={attributes.isLoading}
        action={
          canAdd && (
            <Button
              variant="contained"
              startIcon={<AddRounded />}
              onClick={() => setEditing('new')}
            >
              New attribute
            </Button>
          )
        }
        empty={
          list.length === 0 ? (
            <EmptyState
              icon={<TuneRounded />}
              title="No attributes yet"
              body="Add Size or Finish once and pick values on every variant instead of typing them."
              action={
                canAdd ? { label: 'Add an attribute', onClick: () => setEditing('new') } : undefined
              }
            />
          ) : null
        }
      >
        {list.map((a) => (
          <Box
            key={a.id}
            sx={{ borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}
          >
            <Stack direction="row" spacing={1.5} sx={{ px: 2.5, pt: 2, alignItems: 'center' }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ fontWeight: 650 }}>
                    {a.name}
                  </Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={
                      a.dataType === 'OPTION'
                        ? 'Options'
                        : a.dataType === 'NUMBER'
                          ? 'Number'
                          : 'Text'
                    }
                    sx={{ height: 20, fontSize: 11 }}
                  />
                  {a.unitHint && (
                    <Typography variant="caption" color="text.secondary">
                      unit: {a.unitHint}
                    </Typography>
                  )}
                  {!a.isActive && (
                    <Chip size="small" label="Inactive" sx={{ height: 20, fontSize: 11 }} />
                  )}
                </Stack>
              </Box>
              {canEdit && <EditButton label={`Edit ${a.name}`} onClick={() => setEditing(a)} />}
            </Stack>
            <Stack
              direction="row"
              spacing={0.75}
              sx={{
                px: 2.5,
                pt: 1.25,
                pb: 2,
                flexWrap: 'wrap',
                rowGap: 0.75,
                alignItems: 'center',
              }}
            >
              {a.dataType !== 'OPTION' ? (
                <Typography variant="caption" color="text.secondary">
                  Typed on each variant{a.dataType === 'NUMBER' ? ' as a number' : ''}.
                </Typography>
              ) : (
                <>
                  {a.options.map((o) => (
                    <Chip
                      key={o.id}
                      label={o.value}
                      size="small"
                      variant={o.isActive ? 'filled' : 'outlined'}
                      sx={
                        o.isActive
                          ? undefined
                          : { textDecoration: 'line-through', color: 'text.disabled' }
                      }
                      onDelete={
                        canEdit
                          ? () =>
                              toggleOption.mutate({
                                attribute: a,
                                optionId: o.id,
                                isActive: !o.isActive,
                              })
                          : undefined
                      }
                      deleteIcon={
                        canEdit ? (
                          <Tooltip title={o.isActive ? 'Retire option' : 'Bring back'}>
                            {o.isActive ? <DeleteOutlineRounded /> : <AddRounded />}
                          </Tooltip>
                        ) : undefined
                      }
                    />
                  ))}
                  {canEdit &&
                    (adding?.id === a.id ? (
                      <Box
                        component="form"
                        onSubmit={(e) => {
                          e.preventDefault();
                          const value = optionText.trim();
                          if (value) addOption.mutate({ attribute: a, value });
                        }}
                        sx={{ display: 'inline-flex', gap: 0.75, alignItems: 'center' }}
                      >
                        <TextField
                          size="small"
                          autoFocus
                          value={optionText}
                          onChange={(e) => setOptionText(e.target.value)}
                          placeholder="New option"
                          onKeyDown={(e) => {
                            if (e.key === 'Escape') setAdding(null);
                          }}
                          slotProps={{
                            htmlInput: { maxLength: 80, 'aria-label': `New ${a.name} option` },
                          }}
                          sx={{ width: 150, '& .MuiInputBase-root': { height: 30 } }}
                        />
                        <Button size="small" type="submit" loading={addOption.isPending}>
                          Add
                        </Button>
                      </Box>
                    ) : (
                      <Chip
                        size="small"
                        icon={<AddRounded />}
                        label="Option"
                        variant="outlined"
                        color="primary"
                        onClick={() => {
                          setOptionText('');
                          setAdding(a);
                        }}
                      />
                    ))}
                </>
              )}
            </Stack>
          </Box>
        ))}
      </ListCard>
      <AttributeDialog
        open={editing !== null}
        attribute={editing === 'new' ? null : editing}
        onClose={() => setEditing(null)}
      />
    </>
  );
}

function AttributeDialog({
  open,
  attribute,
  onClose,
}: {
  open: boolean;
  attribute: Attribute | null;
  onClose: () => void;
}) {
  const notify = useNotify();
  const qc = useQueryClient();
  const initial = (): AttributeFormValues => ({
    name: attribute?.name ?? '',
    dataType: attribute?.dataType ?? 'OPTION',
    unitHint: attribute?.unitHint ?? '',
    options: '',
    isActive: attribute?.isActive ?? true,
  });
  const { register, control, handleSubmit, reset, setError, formState } =
    useForm<AttributeFormValues>({
      resolver: zodResolver(attributeFormSchema),
      defaultValues: initial(),
    });
  useEffect(() => {
    if (open) reset(initial());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset when opened
  }, [open, attribute]);
  const dataType = useWatch({ control, name: 'dataType' });
  const save = useMutation({
    mutationFn: (v: AttributeFormValues) =>
      attribute
        ? unwrap(
            api.PATCH('/attributes/{id}', {
              params: { path: { id: attribute.id } },
              body: {
                name: v.name.trim(),
                unitHint: v.unitHint.trim() || null,
                isActive: v.isActive,
                version: attribute.version,
              },
            }),
          )
        : unwrap(
            api.POST('/attributes', {
              params: { header: idempotency() },
              body: {
                name: v.name.trim(),
                dataType: v.dataType,
                unitHint: v.unitHint.trim() || null,
                ...(v.dataType === 'OPTION' ? { options: optionsFromText(v.options) } : {}),
              },
            }),
          ),
    onSuccess: async (saved) => {
      notify.success(attribute ? `${saved.name} saved` : `${saved.name} added`);
      await qc.invalidateQueries({ queryKey: catalogKeys.attributes });
      onClose();
    },
    onError: (e) => {
      if (
        !applyFieldErrors(e, ['name', 'unitHint', 'options'], (f, m) =>
          setError(f as Path<AttributeFormValues>, m),
        )
      )
        notify.error(e);
    },
  });
  const { errors } = formState;
  return (
    <SimpleDialog
      open={open}
      title={attribute ? `Edit ${attribute.name}` : 'New attribute'}
      busy={save.isPending}
      submitLabel={attribute ? 'Save' : 'Add attribute'}
      onClose={onClose}
      onSubmit={() => void handleSubmit((v) => save.mutate(v))()}
    >
      <TextField
        label="Name"
        autoFocus
        required
        placeholder="Size"
        {...register('name')}
        error={!!errors.name}
        helperText={errors.name?.message}
      />
      <Stack direction="row" spacing={2}>
        <Controller
          control={control}
          name="dataType"
          render={({ field }) => (
            <TextField
              select
              fullWidth
              label="Values are"
              value={field.value}
              onChange={field.onChange}
              disabled={!!attribute}
              helperText={attribute ? 'Fixed once created' : undefined}
            >
              <MenuItem value="OPTION">Picked from a list</MenuItem>
              <MenuItem value="NUMBER">A number</MenuItem>
              <MenuItem value="TEXT">Free text</MenuItem>
            </TextField>
          )}
        />
        <TextField
          label="Unit"
          placeholder="mm"
          {...register('unitHint')}
          error={!!errors.unitHint}
          helperText={errors.unitHint?.message ?? 'Optional'}
          sx={{ width: 120, flexShrink: 0 }}
        />
      </Stack>
      {!attribute && dataType === 'OPTION' && (
        <TextField
          label="Options"
          multiline
          minRows={3}
          placeholder={'24x24\n12x24\n16x16'}
          {...register('options')}
          error={!!errors.options}
          helperText={
            errors.options?.message ?? 'One per line (or comma-separated). You can add more later.'
          }
        />
      )}
      {attribute && (
        <Controller
          control={control}
          name="isActive"
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch checked={field.value} onChange={(e) => field.onChange(e.target.checked)} />
              }
              label="Active — offered on variants"
            />
          )}
        />
      )}
    </SimpleDialog>
  );
}
